import { injectable } from 'inversify';
import groupBy from 'lodash/groupBy';

import { t } from '@vk-hr-tek/core/translations';
import { FormatService } from '@vk-hr-tek/core/format';
import { ChipColorUnion } from '@vk-hr-tek/ui/ColoredChip';
import { AssistChipColor } from '@vk-hr-tek/ui/ColoredAssistChip/ColoredAssistChip.types';
import { Filter } from '@vk-hr-tek/core/filter';

import {
  GetAccountWidgetBannerResponse,
  SlideItemConstruct,
  Widget,
  NewsData,
} from '@app/gen/dashboard';
import {
  EventStat,
  GroupUser,
  ManagerSubstitutesEmployee,
  CompanyEmployee,
  ManagerSubstitutesEventType,
  Validator,
  EmployeeIdWithSignType,
  CompanyGroupUsers,
  CompanyProfiles,
  Group,
  WorkflowParticipant,
  WorkflowParticipantAction,
  EditableCompany,
  CertificateError,
  RoleModelDomain,
  ManagerSubstitute,
  GetCompanyCeoResponse,
  CompanyTimezone,
  TimezoneException,
  FilterSelectOption,
  TimezoneEmployee,
} from '@app/gen/settings';
import { EmployeeListItem, UserListItem } from '@app/gen/users';
import { SignTypes, SignType } from '@app/types';

import {
  SignTypeSource,
  CompanyGroup,
  Status,
  ParticipantsAction,
  SubstituteType,
  ManagerSubstitutesEventTypeItem,
  FormDeadlineReference,
} from '../../types';
import {
  CEO_FIELDS,
  DEADLINE_OPTIONS,
  FIXED_WIDGET_SIZE,
  RoleModelNameEnum,
  WIDGET_TITLES,
} from '../../constants';

const COLORS: Record<string, ChipColorUnion> = {
  upload: 'orange',
  generate_document_from_template: 'orange',
  return: 'orange',
  accept: 'purple',
  system: 'gray',
  decline: 'red',
  sign: 'blue',
};

const LABELS: Record<string, string> = {
  upload: 'Загрузка',
  generate_document_from_template: 'Загрузка',
  accept: 'Проверка',
  system: 'Обработка',
  decline: 'Отказ',
  sign: 'Подписание',
  return: 'Доработка',
};

const getGroupName = (group: Group | undefined, groups: Group[]): string => {
  if (!group) return '';

  if (group.parent_id) {
    const parent = groups.find(({ id }) => id === group.parent_id);

    return `${group.name} / ${getGroupName(parent, groups)}`;
  }

  return group.name;
};

const MAX_NEWS_COUNT_TYPE_1_4 = 5;
const MAX_NEWS_COUNT_TYPE_4 = 4;

@injectable()
export class SettingsDetailMapper {
  constructor(private format: FormatService) {}

  eventStat(number: number) {
    return {
      text: this.format.convertNumber(number),
      title: number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' '),
    };
  }

  processEventStat(eventStats: EventStat[]) {
    return eventStats.map((event) => ({
      id: event.event_type.id,
      name: event.event_type.name,
      collections: event.collections,
      active: this.eventStat(event.stat.active),
      finished: this.eventStat(event.stat.finished),
      canceled: this.eventStat(event.stat.canceled),
      inPaper: this.eventStat(event.stat.in_paper),
      total: this.eventStat(event.stat.total),
      disabled: event.event_type.disabled,
    }));
  }

  processCompaniesListByAccount(companies: EditableCompany[]) {
    return companies.map((company) => ({
      id: company.company_item.id,
      name: company.company_item.name,
      licenseTotal: company.employee_limit,
      licenseUsed: company.employee_count,
      signTypes: company.sign_type,
      users: company.user_with_sign_type,
      importOperationalUnits: company.import_operational_units,
      importLegalUnits: company.import_legal_units,
      payslipsEnabled: company.payslips_enabled,
      employeeSignTypesSource: company.employee_sign_type_source,
      availableSignTypess: company.available_sign_types,
    }));
  }

  processCompanyGroupUser(companyGroupUsers: CompanyGroupUsers) {
    return companyGroupUsers?.group_users.map((group) => ({
      id: group.group.id,
      name: group.group.name,
      users: group.users.map((user) => this.processUser(user)),
      profilesEnabled: group.employee_profiles_enabled,
    }));
  }

  processUser(user: GroupUser) {
    const companiesNames =
      user.employees
        ?.map((employee) =>
          `${employee?.company?.name ?? ''} (${
            employee.personnel_number
          })`.trim(),
        )
        .join(', ') || '';

    return {
      id: user.id,
      name: `${user.last_name} ${user.first_name} ${user.middle_name}`,
      companiesNames,
      profiles: user.profiles,
      editable: user.editable,
    };
  }

  processUserList(users: UserListItem[]) {
    return users
      .filter((user) => user.employees.length > 0)
      .map((user) => {
        const companiesNames = user.employees
          .map((employee) =>
            `${employee?.company?.name ?? ''} (${
              employee.personnel_number
            })`.trim(),
          )
          .join(', ');

        const name = `${user.last_name} ${user.first_name} ${user.middle_name}`;

        return {
          label: `${name}, ${companiesNames}`,
          value: user.id,
        };
      });
  }

  processProfileList(profiles: CompanyProfiles[], companyId: string) {
    const profile = profiles.find(({ company }) => company.id === companyId);

    return (profile?.profiles || []).map(({ id, name }) => ({
      label: name,
      value: id,
    }));
  }

  processManagerSubstitutes(employees: ManagerSubstitutesEmployee[]) {
    return employees.map(({ employee, substitutes_count }) => ({
      employee: this.processEmployees(employee),
      substitutesCount: substitutes_count,
    }));
  }

  processManagerSubstitutesEventType(
    event_types: ManagerSubstitutesEventType[],
  ) {
    return event_types.map(({ substitutes, event_type }) => ({
      substitutes: substitutes.map((substitute) => ({
        ...this.processEmployeesForEventType(substitute),
      })),
      eventType: {
        id: event_type.id,
        name: event_type.name,
      },
    }));
  }

  processGroupedEventTypesByManagerSubstitute(
    managerSubstitutesEventTypes: ManagerSubstitutesEventTypeItem[],
  ) {
    const groupedEventTypesByManagerSubstitute: Record<
      string,
      Record<string, SubstituteType>
    > = {};

    managerSubstitutesEventTypes.forEach(({ substitutes, eventType }) => {
      substitutes.forEach(({ id, dates }) => {
        groupedEventTypesByManagerSubstitute[id] = {
          ...groupedEventTypesByManagerSubstitute[id],
          [eventType.id]: !!dates?.length ? 'temporary' : 'permanent',
        };
      });
    });

    return groupedEventTypesByManagerSubstitute;
  }

  processEmployeesForEventType(employee: ManagerSubstitute) {
    const { substitute, dates, available_options: availableOptions } = employee;

    const sortedAvailableOptions = availableOptions && [
      ...availableOptions.filter((option) => option.scope === 'ALL'),
      ...availableOptions.filter((option) => option.scope !== 'ALL'),
    ];

    return {
      id: substitute.id,
      name: substitute.name,
      personnelNumber: substitute.personnel_number,
      email: substitute.email,
      position: substitute.position,
      phone: substitute.phone,
      company: {
        id: substitute.company.id,
        name: substitute.company.name,
      },
      availableOptions: sortedAvailableOptions,
      unit: substitute.unit_name,
      dates: dates || [],
    };
  }

  processEmployees(employee: CompanyEmployee) {
    return {
      id: employee.id,
      name: employee.name,
      personnelNumber: employee.personnel_number,
      email: employee.email,
      position: employee.position,
      phone: employee.phone,
      company: {
        id: employee.company.id,
        name: employee.company.name,
      },
    };
  }

  transformValidators(validators: Validator[] | undefined) {
    const newValidators = validators?.reduce<Record<string, Validator[]>>(
      (acc, cur) => {
        const idByAttrib = [...cur.attributes].sort().join('/');

        acc[idByAttrib] = acc[idByAttrib] ? [...acc[idByAttrib], cur] : [cur];
        return acc;
      },
      {},
    );

    return newValidators;
  }

  transformCompanySettingsEmployeeOptions(
    employees:
      | EmployeeIdWithSignType[]
      | EmployeeListItem[]
      | CompanyEmployee[],
    fn?: (
      employee: EmployeeIdWithSignType | EmployeeListItem | CompanyEmployee,
    ) => boolean,
  ) {
    return employees.map((employee) => {
      const label = `${employee.last_name} ${employee.first_name} ${employee.middle_name}`;

      return {
        label,
        value: employee.id,
        prefix: employee.personnel_number,
        disabled: fn ? fn(employee) : false,
        tooltipText: employee.sign_type
          ? SignTypes[employee.sign_type]
          : undefined,
      };
    });
  }

  private getDiffSignTypeSEmployeesOptions = (
    employees: EmployeeIdWithSignType[] = [],
  ) => {
    const employeesWithGoskey = employees.filter(
      (employee) => employee.sign_type === 'goskey',
    );
    const employeesWithCryptoproLocal = employees.filter(
      (employee) => employee.sign_type === 'cryptopro_local',
    );
    const employeesWithCryptoproSimple = employees.filter(
      (employee) => employee.sign_type === 'cryptopro_simple',
    );
    const employeesWithKontur = employees.filter(
      (employee) => employee.sign_type === 'kontur',
    );
    const employeesWithUkep = employees.filter(
      (employee) => employee.sign_type === 'ukep',
    );

    return {
      employeesWithGoskey: this.transformCompanySettingsEmployeeOptions(
        employeesWithGoskey,
      ).map((employee) => employee.value),
      employeesWithCryptoproLocal: this.transformCompanySettingsEmployeeOptions(
        employeesWithCryptoproLocal,
      ).map((employee) => employee.value),
      employeesWithCryptoproSimple:
        this.transformCompanySettingsEmployeeOptions(
          employeesWithCryptoproSimple,
        ).map((employee) => employee.value),
      employeesWithKontur: this.transformCompanySettingsEmployeeOptions(
        employeesWithKontur,
      ).map((employee) => employee.value),
      employeesWithUkep: this.transformCompanySettingsEmployeeOptions(
        employeesWithUkep,
      ).map((employee) => employee.value),
    };
  };

  private getDisabledSignTypeEmployeesOptions = (
    employees: EmployeeIdWithSignType[] = [],
  ) => {
    const disabledSignTypeEmployees = employees.filter(
      (employee) => employee.sign_type === 'disabled',
    );
    return this.transformCompanySettingsEmployeeOptions(
      disabledSignTypeEmployees,
    );
  };

  transformCompanySignSettingsInitialValues(
    companyId: string,
    signType: SignType,
    employeeSignTypeSource: SignTypeSource,
    employees: EmployeeIdWithSignType[],
  ) {
    return {
      companyId,
      signType,
      employeesWithoutSign: this.getDisabledSignTypeEmployeesOptions(
        employees,
      ).map((employee) => employee.value),
      employeeSignTypeSource,
      ...this.getDiffSignTypeSEmployeesOptions(employees),
    };
  }

  transformToTreeCompanyGroup = (groups: Group[]): CompanyGroup[] => {
    const grouped = groupBy(groups, (item) => item.parent_id);

    const childrenOf = (parentId: string): CompanyGroup[] => {
      if (grouped[parentId] === undefined) {
        return [];
      }

      return grouped[parentId].map(({ id, name }) => ({
        id,
        label: name,
        children: childrenOf(id),
      }));
    };

    return childrenOf('') as CompanyGroup[];
  };

  combineStatuses = (...statuses: Status[]): Status => {
    if (statuses.every((status: Status) => status === 'idle')) {
      return 'idle';
    }
    if (statuses.every((status: Status) => status === 'complete')) {
      return 'complete';
    }
    if (statuses.some((status: Status) => status === 'failed')) {
      return 'failed';
    }
    if (statuses.some((status: Status) => status === 'loading')) {
      return 'loading';
    }
    return 'failed';
  };

  transformAvailableSignTypesToOptions = (
    availableSignTypes: string[],
  ): { value: SignType; label: string }[] => {
    return availableSignTypes
      .filter((signType) => signType !== 'disabled')
      .map((signType) => {
        return {
          label: SignTypes[signType as SignType],
          value: signType as SignType,
          badge: {
            title: signType === 'ukep' ? 'УКЭП' : 'УНЭП',
            color: signType === 'ukep' ? 'velvet' : 'blue',
          },
        };
      });
  };

  getOtherUnepType = (
    typeOptions: { label: string; value: SignType }[],
    unepType: SignType,
  ): SignType => {
    const remainType = typeOptions.find(({ value }) => {
      return value !== unepType;
    });

    return remainType?.value || unepType;
  };

  getDeadlineReferenceOptions = (reference?: FormDeadlineReference) => {
    switch (reference) {
      case 'event_deadline':
        return [
          ...DEADLINE_OPTIONS,
          {
            value: 'event_deadline',
            label: t(
              'settings.eventTypes.editDeadline.fromEventDeadlineDateText',
            ),
          },
        ];
      case 'created_datetime':
        return [
          ...DEADLINE_OPTIONS.filter(
            (option) => option.value !== 'created_date',
          ),
          { value: 'created_datetime', label: 'От момента создания заявки' },
        ];
      default:
        return DEADLINE_OPTIONS;
    }
  };

  getSignTypeLabel = (signType: SignType): string => {
    if (
      ['kontur', 'cryptopro_simple', 'cryptopro_local', 'goskey'].includes(
        signType,
      )
    ) {
      return `${t('settings.mappers.settingsDetail.employeesUsingUNEP')} ${
        SignTypes[signType]
      }`;
    } else if (signType === 'ukep') {
      return t('settings.mappers.settingsDetail.employeesWithAnotherECP');
    } else {
      return t('settings.mappers.settingsDetail.employeesWithoutUNEP');
    }
  };

  countOtherSignTypes = (
    users: EmployeeIdWithSignType[],
    signType: SignType,
    availableSignTypes: string[],
  ): { count: number; type: SignType }[] => {
    const otherTypes = (availableSignTypes as SignType[]).filter(
      (type) => type !== signType,
    );

    return otherTypes.map((type) => ({
      type,
      count: users.filter((user) => user.sign_type === type).length,
    }));
  };

  processAction = (
    actions: WorkflowParticipantAction[],
  ): ParticipantsAction[] => {
    let uploadCount = 0;

    return actions.reduce((acc, action, index) => {
      if (
        action.action_type === 'generate_document_from_template' ||
        action.action_type === 'upload'
      ) {
        uploadCount += action.count;
      } else {
        acc = [
          ...acc,
          {
            count: action.count,
            color: COLORS[action.action_type],
            label: LABELS[action.action_type],
          },
        ];
      }
      if (index + 1 === actions.length && uploadCount) {
        acc = [
          ...acc,
          {
            count: uploadCount,
            color: COLORS.upload,
            label: LABELS.upload,
          },
        ];
      }
      return acc;
    }, [] as ParticipantsAction[]);
  };

  processParticipant(participant: WorkflowParticipant) {
    return {
      id: participant.id.join(''),
      group: {
        group: participant.group,
        employee: participant.employee,
        role: participant.role,
        levels: participant.levels,
        number: participant.number,
        editable: participant.editable,
      },
      actions: {
        group: participant.group,
        employee: participant.employee,
        role: participant.role,
        actions:
          participant.actions.length > 0
            ? this.processAction(participant.actions)
            : [],
        allowDelete: participant.allow_delete,
        ids: participant.id,
      },
    };
  }

  private processSmevErrorsLevel(level: 'fatal' | 'warning'): {
    label: string;
    statusChipColor: ChipColorUnion;
  } {
    switch (level) {
      case 'fatal':
        return { label: 'Высокая', statusChipColor: 'red' };
      case 'warning':
        return { label: 'Средняя', statusChipColor: 'orange' };
      default:
        return {
          label: '',
          statusChipColor: 'gray',
        };
    }
  }

  processSmevErrors(smevError: CertificateError) {
    return {
      id: smevError.id,
      level: this.processSmevErrorsLevel(smevError.level),
      description: {
        label:
          smevError.description[0].toUpperCase() +
          smevError.description.substring(1),
      },
      ignore: {
        id: smevError.id,
        ignore: smevError.ignore,
      },
    };
  }

  processGroups(groups: Group[]) {
    return groups.map((group) => ({
      label: getGroupName(group, groups),
      value: group.id,
    }));
  }

  processCompanyRoleModel(domains: RoleModelDomain[], groups: Group[]) {
    const allCompanyGroupsValue = 8;

    const formattedCompanyGroups = [
      {
        label: 'Все специалисты',
        value: '00000000-0000-0000-0000-000000000000',
      },
      ...this.processGroups(groups),
    ];

    const initialValues: Record<string, string[] | number> = {};
    const roleModel = domains
      .map((domain, index) => {
        const formattedAllEmployeesValues = domain.employees_values.map(
          ({ name, value }) => ({
            value,
            label: name,
          }),
        );
        const formattedManagersValues = domain.managers_values.map(
          ({ name, value }) => ({
            value,
            label: name,
          }),
        );

        return [
          {
            id: index,
            type: RoleModelNameEnum.TitleRow,
            name: {
              text: this.processRoleModelName(domain),
              type: RoleModelNameEnum.TitleRow,
            },
            tooltip: domain.tooltip,
            employees: null,
            managers: null,
            companyGroups: null,
          },
          ...(domain.objects
            ? domain.objects
                .filter(({ id, name }) => id && name)
                .map(({ id, name, access_control: accessControl, tooltip }) => {
                  const employees = accessControl?.find(
                    ({ type }) => type === 'employees',
                  );
                  const managers = accessControl?.find(
                    ({ type }) => type === 'managers',
                  );
                  const companyGroups = accessControl?.find(
                    ({ type }) => type === 'company_groups',
                  );
                  const employeesFieldName = `${id}:employees`;
                  const managersFieldName = `${id}:managers`;
                  const companyGroupsFieldName = `${id}:company_groups`;

                  initialValues[employeesFieldName] = employees?.value || 0;
                  initialValues[managersFieldName] = managers?.value || 0;

                  if (companyGroups?.group_ids) {
                    initialValues[companyGroupsFieldName] =
                      companyGroups.group_ids;
                  } else if (companyGroups?.value === allCompanyGroupsValue) {
                    initialValues[companyGroupsFieldName] =
                      formattedCompanyGroups.map((group) => group.value);
                  } else {
                    initialValues[companyGroupsFieldName] = [];
                  }

                  return {
                    id,
                    tooltip,
                    name: {
                      text: name,
                      type: RoleModelNameEnum.Default,
                    },
                    employees: {
                      enabled: employees?.enabled || false,
                      options: formattedAllEmployeesValues,
                      fieldName: employeesFieldName,
                    },
                    managers: {
                      enabled: managers?.enabled || false,
                      options: formattedManagersValues,
                      fieldName: managersFieldName,
                    },
                    companyGroups: {
                      enabled: companyGroups?.enabled || false,
                      options: formattedCompanyGroups,
                      fieldName: companyGroupsFieldName,
                    },
                  };
                })
            : []),
        ];
      })
      .flat();

    return { roleModel, initialValues };
  }

  private processRoleModelName(domain: RoleModelDomain) {
    return `${domain.name}${
      domain.group_name ? ` - ${domain.group_name}` : ''
    }`;
  }

  processCompanyCeo(companyCeoData: GetCompanyCeoResponse) {
    return CEO_FIELDS.reduce((acc, field) => {
      const cases = companyCeoData[`${field}_cases`];
      const nominativeValue = companyCeoData[field];

      return {
        ...acc,
        [field]: nominativeValue,
        [`${field}_cases`]: cases,
      };
    }, {});
  }

  processCompanyTimezoneExceptions(items: TimezoneException[]) {
    return items.map(({ timezone_id, timezone_name, employee }) => {
      return {
        id: employee.id,
        employee: this.processTimezoneExceptionsEmployee(employee),
        timezone: {
          timezoneId: timezone_id,
          timezoneName: timezone_name,
        },
      };
    });
  }

  processCompanyTimezoneEmployee(items: TimezoneEmployee[]) {
    return items.map(
      ({ timezone_id, timezone_name, employee, status, description }) => {
        return {
          id: employee.id,
          employee: this.processTimezoneExceptionsEmployee(employee),
          timezone: {
            timezoneId: timezone_id,
            timezoneName: timezone_name,
          },
          status,
          description,
        };
      },
    );
  }

  processSelectedTimezoneExceptions(
    items: TimezoneException[],
    selected: string[],
  ) {
    return items
      .filter((item) => selected.includes(item.employee.id))
      .map((item) => ({
        id: item.employee.id,
        fullName: `${item.employee.last_name} ${item.employee.first_name} ${item.employee.middle_name}`,
        personnelNumber: item.employee.personnel_number,
        unitName: item.employee.unit_name,
        timezoneId: item.timezone_id,
        timezoneName: item.timezone_name,
        companyName: item.employee.company.name,
        companyId: item.employee.company.id,
      }));
  }

  processTimezoneExceptionsEmployee(employee: CompanyEmployee) {
    return {
      id: employee.id,
      fullName: `${employee.last_name} ${employee.first_name} ${employee.middle_name}`,
      avatar: `${employee.last_name[0]}${employee.first_name[0]}`,
      personnelNumber: employee.personnel_number,
      unitName: employee.unit_name,
      orgStructureType: employee.org_structure_type,
    };
  }

  processCompanyTimezones(timezones: CompanyTimezone[]) {
    if (!timezones.length) return [];
    return [
      { label: 'Все', id: 'all', color: AssistChipColor.blue },
      ...timezones.map(({ id, name, employee_count }) => ({
        id,
        label: name,
        counter: employee_count,
        color: AssistChipColor.blue,
      })),
    ];
  }

  processDisabledTimezoneOptions(
    timezoneOptions: FilterSelectOption[],
    disabledTooltipText: string,
    selectedOptions?: string[],
    companyTimezoneId?: string,
  ) {
    const idsToDisable = new Set(selectedOptions ?? []);

    const optionsWithoutCompanyTimezone = timezoneOptions.filter(
      (option) => option.value !== companyTimezoneId,
    );

    return optionsWithoutCompanyTimezone.map(({ value, label }) => ({
      value,
      label,
      ...(idsToDisable.has(value) && {
        tooltipText: disabledTooltipText,
        disabled: idsToDisable.has(value),
      }),
    }));
  }

  processTimezoneExceptionsFilters(filters: Filter[]) {
    if (!filters) return null;

    return filters
      .map((filter) => {
        if (
          filter.type === 'multiple-search' &&
          filter.key === 'employee_ids'
        ) {
          return {
            ...filter,
            twoLine: true,
            minInputValueLength: 3,
          };
        }

        if (filter.type === 'multiple-search' && filter.key === 'unit_ids') {
          return {
            ...filter,
            // eslint-disable-next-line no-magic-numbers
            width: 8 as const,
            fullWidth: false,
          };
        }

        return {
          ...filter,
          fullWidth: false,
        };
      })
      .filter(({ key }) => key !== 'timezone_ids');
  }

  processWidgets(widgets: Widget[]) {
    return widgets.map((widget) => ({
      id: widget.id,
      type: widget.type,
      name: widget.custom_name || WIDGET_TITLES[widget.type],
      x: widget.position.x || 0,
      y: widget.position.y || 0,
      size:
        (widget.type &&
          widget.type in FIXED_WIDGET_SIZE &&
          FIXED_WIDGET_SIZE[widget.type]) ||
        (widget.size as number) ||
        1,
      isActive: widget.is_active,
      switchable: JSON.stringify(!!widget.settings?.user_switchable),
      hasSettings: JSON.stringify(!!widget.settings?.user_editable),
      hasFilters: JSON.stringify(!!widget.settings?.has_filter),
      linked: !widget?.company_content_id,
      isCustom: !widget?.source_widget_id,
      isContentFilled: widget.settings?.is_content_filled ?? false,
    }));
  }

  processBanner(bannerWidget: GetAccountWidgetBannerResponse) {
    const maxSlidesLength = 3;
    const { type, slides: slidesData } = bannerWidget.banner;
    const slides =
      slidesData?.slice(0, maxSlidesLength).map((slide, index) => {
        if (type === 'simple') {
          const { url, background_image_id: background } = slide;
          return {
            id: index,
            url,
            background: { id: background },
            buttonType: 'primary' as const,
            textColor: 'black' as const,
          };
        }
        const {
          background_image_id: background,
          additional_image_id: image,
          button_type: buttonType,
          button_text: buttonText,
          text_color: textColor,
          ...rest
        } = slide as SlideItemConstruct;

        return {
          ...rest,
          id: index,
          background: { id: background },
          image: { id: image },
          buttonType: buttonType || 'primary',
          buttonText: buttonText || '',
          textColor: textColor || 'black',
        };
      }) || [];

    return {
      name: bannerWidget.name,
      type: type || 'simple',
      slides,
    };
  }

  processTopNewsPreview(
    topNews: { type: 'type_1_4' | 'type_4'; items: NewsData[] } | null,
  ) {
    if (!topNews) return null;

    const { items, type } = topNews;
    if (!type || !items || items.length === 0) return null;

    const requiredCount =
      type === 'type_1_4' ? MAX_NEWS_COUNT_TYPE_1_4 : MAX_NEWS_COUNT_TYPE_4;

    if (items.length < requiredCount) return null;

    const trimmedNews = items.slice(0, requiredCount).map((item) => ({
      id: item.id,
      title: item.title,
      announcement: item.announcement,
      coverId: item.cover_att_id,
      category: item.category,
      publishedAt: item.published_at,
      isImportant: item.is_important,
    }));

    return {
      type,
      news: trimmedNews,
    };
  }
}
